import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import MailInputComponent from "./mailInputComponent";
import MultiTourSelection from "./MultiTourSelector";
import services from "../../services/services";
import { toast } from "react-toastify";
import { OtherMailDataType } from "./OtherEmail";

export type MailDataType = {
  id: number;
  duration: string;
  minElapsedTime?: string;
  template: NewTemplateObj[];
  tourlist: string;
  bundlelist: string;
  isEditMode: boolean;
  transportation: number;
  error: string;
};

export type NewTemplateObj = {
  language: string;
  htmlTemplate: string;
  error: string;
  isRendering: boolean;
  subject: string;
  id: number;
};

function MailTemplate({ mailType }: { mailType: "redeem" | "global" | "upsell" }) {
  const [mailData, setMailData] = useState<MailDataType[]>([]);
  const [collapsedCards, setCollapsedCards] = useState<Record<number, boolean>>({});
  const { Tours, isLoading, isError } = useSelector(
    (state: any) => state.tours
  );
  const {
    Bundles,
    isLoading: bundleLoading,
    isError: bundleError,
  } = useSelector((state: any) => state.getBundles);
  const [reloadData, setReloadData] = useState(false);
  const [templateTours, setTemplateTours] = useState<any[]>([]);
  const [templateBundles, setTemplateBundles] = useState<any[]>([]);
  const [emailContents, setEmailContent] = useState<any[]>([]);

  const toggleCollapse = (id: number) => {
    setCollapsedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const transformData = async () => {
      try {
        const response = await services.getAllMailData(mailType);
        const mailArray = Array.isArray(response) ? response : response?.data;
        if (!mailArray || !Array.isArray(mailArray)) {
          console.error("Mail data response is missing or not an array.");
          return;
        }
        setMailData(
          mailArray.map((item: any) => ({
            ...item,
            template: typeof item.template === 'string' ? JSON.parse(item.template) : item.template,
            isEditMode: false,
            error: "",
          }))
        );
      } catch (error: any) {
        toast.error("Something went wrong while fetching mail");
        console.error("Error fetching mail data:", error.message);
      }
    };

    transformData();
  }, [reloadData, mailType]);

  const transformInitialState = (
    entityList: any[],
    type: "tour" | "bundle",
    idlist: number[]
  ) => {
    let transformedData = [];
    if (mailType == "global") {
      transformedData = mailData.map((item) => {
        const curTourlist = item.tourlist.split("-");
        const curBundlelist = item.bundlelist.split("-");
        console.log("curtourlist", curTourlist, idlist);
        const selectedData = item[type === "tour" ? "tourlist" : "bundlelist"]
          ? item[type === "tour" ? "tourlist" : "bundlelist"]
              .split("-")
              .map((id: string) => parseInt(id, 10))
          : [];
        const allList = Array.isArray(entityList)
          ? entityList
              .filter((elm) => {
                return (
                  (type === "tour" ? curTourlist : curBundlelist).includes(
                    String(elm.id)
                  ) || !idlist.includes(elm.id)
                );
              })
              .map((entity: any) => ({
                name: entity?.name || `Unknown ${type}`,
                [type === "tour" ? "tourId" : "bundleId"]: entity?.id || -1,
                isChecked: selectedData.includes(entity?.id),
              }))
          : [];
        return {
          templateId: item.id,
          [type === "tour" ? "tours" : "bundles"]: allList,
        };
      });
    } else {
      transformedData = mailData.map((item) => {
        const selectedData = item[type === "tour" ? "tourlist" : "bundlelist"]
          ? item[type === "tour" ? "tourlist" : "bundlelist"]
              .split("-")
              .map((id: string) => parseInt(id, 10))
          : [];
        const allList = Array.isArray(entityList)
          ? entityList.map((entity: any) => ({
              name: entity?.name || `Unknown ${type}`,
              [type === "tour" ? "tourId" : "bundleId"]: entity?.id || -1,
              isChecked: selectedData.includes(entity?.id),
            }))
          : [];
        return {
          templateId: item.id,
          [type === "tour" ? "tours" : "bundles"]: allList,
        };
      });
    }
    console.log("here is transform data", transformedData);
    if (type === "tour") {
      setTemplateTours(transformedData);
    } else {
      setTemplateBundles(transformedData);
    }
  };

  // Initialize email content as soon as mail data is available (no Tours dependency)
  useEffect(() => {
    if (mailData.length === 0) return;
    const htmlContentTransform = mailData.map((element) => ({
      templateId: element.id,
      template: element?.template?.map((item) => ({
        ...item,
        isRendering: false,
        error: "",
        id: Math.floor(Math.random() * 3000) + 1,
      })),
    }));
    setEmailContent(htmlContentTransform);
  }, [mailData]);

  // Initialize bundle selections when Bundles data loads
  useEffect(() => {
    if (!Bundles?.data?.data || mailData.length === 0) return;
    let idlist: number[] = [];
    if (mailType === "global") {
      idlist = mailData.reduce((prev: number[], item: MailDataType) => {
        let ar: number[] = item.bundlelist.split("-").map(Number);
        return [...prev, ...ar];
      }, []);
    }
    transformInitialState(Bundles.data.data, "bundle", idlist);
  }, [mailData, Bundles]);

  // Initialize tour selections when Tours data loads
  useEffect(() => {
    if (!Tours?.data?.data || mailData.length === 0) return;
    let idlist: number[] = [];
    if (mailType === "global") {
      idlist = mailData.reduce((prev: number[], item: MailDataType) => {
        let ar: number[] = item.tourlist.split("-").map(Number);
        return [...prev, ...ar];
      }, []);
    }
    transformInitialState(Tours.data.data, "tour", idlist);
  }, [mailData, Tours]);

  const updateMailDataError = (
    id: number,
    errorMessage: string,
    updater: React.Dispatch<React.SetStateAction<MailDataType[]>>
  ) => {
    updater((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, error: errorMessage } : item
      )
    );
  };

  const validateAllTemplate = (templates: NewTemplateObj[]) => {
    return templates.map((item) => {
      const doc = new DOMParser().parseFromString(
        item.htmlTemplate,
        "text/html"
      );
      const isError = doc.querySelector("parsererror");
      return isError
        ? { ...item, htmlTemplate: "" }
        : { ...item, htmlTemplate: doc.documentElement.outerHTML };
    });
  };

  const onTourSelect = (e: any, elm: any, templateData: any) => {
    const tours = templateTours.map((temp) => {
      return temp.templateId === templateData.id
        ? {
            ...temp,
            tours: temp.tours.map((value: any) => {
              return value.tourId === elm.tourId
                ? { ...value, isChecked: e.target.checked }
                : value;
            }),
          }
        : temp;
    });
    setTemplateTours(tours);
  };

  const onBundleSelect = (e: any, elm: any, templateData: any) => {
    const bundles = templateBundles.map((temp) => {
      return temp.templateId === templateData.id
        ? {
            ...temp,
            bundles: temp.bundles.map((value: any) => {
              return value.bundleId === elm.bundleId
                ? { ...value, isChecked: e.target.checked }
                : value;
            }),
          }
        : temp;
    });
    setTemplateBundles(bundles);
  };

  const onTourSelectAll = (templateData: any, alltours: any) => {
    const tours = templateTours.map((temp) => {
      return temp.templateId === templateData.id
        ? {
            ...temp,
            tours: alltours,
          }
        : temp;
    });
    setTemplateTours(tours);
  };

  const onBundleSelectAll = (templateData: any, allBundle: any) => {
    const bundle = templateBundles.map((temp) => {
      return temp.templateId === templateData.id
        ? {
            ...temp,
            bundles: allBundle,
          }
        : temp;
    });
    setTemplateBundles(bundle);
  };

  const onEmailTemplateChange = (
    mailData: MailDataType | OtherMailDataType,
    elm: NewTemplateObj,
    type: string
  ) => {
    const mailDataId = mailData.id;

    if (type === "update") {
      const mailContent = emailContents.map((temp) => {
        if (temp.templateId === mailDataId) {
          const updatedTemplate = temp?.template?.map((value: any) => {
            if (value.id === elm.id) {
              return { ...value, ...elm };
            }
            return value;
          });

          return { ...temp, template: updatedTemplate };
        }
        return temp;
      });
      setEmailContent(mailContent);
    } else if (type === "remove") {
      const mailcontent = emailContents.map((temp) => {
        if (temp.templateId === mailDataId) {
          return {
            ...temp,
            template: temp.template.filter((item: any) => item.id !== elm.id),
          };
        }
        return temp;
      });
      setEmailContent(mailcontent);
    } else if (type === "add") {
      const mailcontent = emailContents.map((temp) => {
        if (temp.templateId === mailDataId) {
          return {
            ...temp,
            template: [...temp.template, elm],
          };
        }
        return temp;
      });
      setEmailContent(mailcontent);
    }
  };

  const addMoreMails = () => {
    const newar: MailDataType[] = [
      {
        id: Math.floor(Math.random() * 3000) + 1,
        duration: "",
        template: [],
        tourlist: "",
        bundlelist: "",
        isEditMode: true,
        error: "",
        minElapsedTime: "",
        transportation: -1, //-1 - refer to none default value
      },
      ...mailData,
    ];
    setMailData(newar);
  };

  const removeEmail = async (id: number) => {
    try {
      await services.deleteMailData(id);
      setMailData(mailData.filter((item) => item.id !== id));
      setReloadData((prev) => !prev);
      toast.success("Deleted Succesfully");
    } catch (error: any) {
      toast.error("Deletion failed");
      console.error(`Error removing email:`, error.message);
    }
  };

  const handleSaveMails = async (elm: MailDataType) => {
    if (!elm.duration) {
      console.error(
        "Invalid duration: must be a positive number and not empty."
      );
      updateMailDataError(elm.id, "Duration must be there.", setMailData);
      return;
    }

    const emailTemps = emailContents.find((elem) => elem.templateId === elm.id);
    if (!emailTemps || !Array.isArray(emailTemps.template)) {
      console.error("No email templates found for mail ID:", elm.id);
      updateMailDataError(
        elm.id,
        "No email templates found for this mail ID.",
        setMailData
      );
      return;
    }
    if (emailTemps.template.length === 0) {
      console.error("Invalid template: must not be empty.");
      updateMailDataError(elm.id, "Template must not be empty.", setMailData);
      return;
    }

    const validatedTemplates = validateAllTemplate(emailTemps.template);
    const invalidTemplates = validatedTemplates.filter(
      (template) => template.htmlTemplate === ""
    );

    if (invalidTemplates.length > 0) {
      console.error("Invalid HTML content found in the template.");
      updateMailDataError(
        elm.id,
        "Invalid HTML content in the template.",
        setMailData
      );
      return;
    }

    const tours = templateTours.find(
      (elem) => elem.templateId === elm.id
    )?.tours;
    const selectedTours = tours
      .filter((item: any) => item.isChecked)
      .map((item: any) => item.tourId)
      .join("-");

    const bundles = templateBundles.find(
      (elem) => elem.templateId === elm.id
    )?.bundles;
    const selectedbundles = bundles
      .filter((item: any) => item.isChecked)
      .map((item: any) => item.bundleId)
      .join("-");

    if (elm.transportation == -1 && !selectedTours && !selectedbundles) {
      console.error("No tours found.");
      updateMailDataError(
        elm.id,
        "No tours and bundles found for this mail.",
        setMailData
      );
      return;
    }
    const payload = {
      duration: elm.duration,
      template: validatedTemplates.map(
        ({ language, subject, htmlTemplate }) => ({
          language,
          subject,
          htmlTemplate,
        })
      ),
      tourlist: selectedTours,
      bundlelist: selectedbundles,
      transportation: elm?.transportation || -1,
      minElapsedTime: mailType === "redeem" ? elm.minElapsedTime : "00:00",
      type: mailType,
    };
    try {
      const response = await services.saveMailData(elm.id, payload);
      updateMailDataError(elm.id, "", setMailData);
      toast.success("Mail data saved successfully");
      setReloadData((prev) => !prev);
    } catch (error: any) {
      console.error("Error saving mail data:", error.message);
      updateMailDataError(
        elm.id,
        "Failed to save mail data. Try again later.",
        setMailData
      );
    }
  };

  const ErrorMessage: React.FC<{
    message: string;
    id: number;
    onClearError: (id: number) => void;
  }> = ({ message, id, onClearError }) => {
    if (!message?.trim()) return null;

    return (
      <div className="bg-red-50 text-red-600 px-4 py-2.5 rounded-lg flex items-center gap-3 border border-red-200 mt-3">
        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
        <span className="text-sm flex-1">{message}</span>
        <button
          className="text-red-400 hover:text-red-600 transition-colors"
          onClick={() => onClearError(id)}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
    );
  };

  const handleClearError = (id: number) => {
    setMailData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, error: "" } : item))
    );
  };

  return (
    <div className="flex flex-col">
      {/* Add Button */}
      <button
        type="button"
        className="self-start flex items-center gap-2 px-4 py-2.5 bg-[#6ECDC5] text-white rounded-lg
          hover:bg-[#5bb8b0] transition-colors duration-200 font-medium text-sm shadow-sm mb-6"
        onClick={addMoreMails}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
        Add New Mail
      </button>

      <div className="flex flex-col gap-5">
        {mailData?.map((item, ind) => {
          const isCollapsed = collapsedCards[item.id];
          return (
            <div
              key={ind}
              className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden transition-shadow hover:shadow-md"
            >
              {/* Card Header */}
              <div
                className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-[#E8F8F5] to-white cursor-pointer"
                onClick={() => toggleCollapse(item.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#6ECDC5] flex items-center justify-center text-white text-sm font-bold">
                    {ind + 1}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-[#1a1a2e] font-roboto">
                      Mail {ind + 1}
                    </h2>
                    {item.duration && (
                      <p className="text-xs text-[#6B7280]">Duration: {item.duration}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Action Buttons */}
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    {!item.isEditMode ? (
                      <button
                        type="button"
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E8F8F5] text-[#6ECDC5] border border-[#6ECDC5]
                          rounded-lg text-sm font-medium hover:bg-[#6ECDC5] hover:text-white transition-colors duration-200"
                        onClick={() => {
                          setMailData((prev) =>
                            prev.map((item, index) =>
                              index === ind
                                ? { ...item, isEditMode: !item.isEditMode }
                                : item
                            )
                          );
                        }}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        Edit
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#6ECDC5] text-white
                          rounded-lg text-sm font-medium hover:bg-[#5bb8b0] transition-colors duration-200 shadow-sm"
                        onClick={() => handleSaveMails(item)}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        Save
                      </button>
                    )}
                    <button
                      type="button"
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-500 border border-red-200
                        rounded-lg text-sm font-medium hover:bg-red-500 hover:text-white transition-colors duration-200"
                      onClick={() => removeEmail(item.id)}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      Delete
                    </button>
                  </div>

                  {/* Collapse Toggle */}
                  <svg
                    className={`w-5 h-5 text-[#6B7280] transition-transform duration-200 ml-2 ${isCollapsed ? '' : 'rotate-180'}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Error Message */}
              {item.error && (
                <div className="px-5">
                  <ErrorMessage
                    message={item.error}
                    id={item.id}
                    onClearError={handleClearError}
                  />
                </div>
              )}

              {/* Card Body (Collapsible) */}
              {!isCollapsed && (
                <div className="px-5 pb-5 pt-3 space-y-5">
                  {/* Duration Setting */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-[#1a1a2e] mb-2">
                      Notification Timing
                    </label>
                    <p className="text-xs text-[#6B7280] mb-3">
                      Set how many hours and minutes before the journey the user should be notified
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center
                            focus:ring-2 focus:ring-[#6ECDC5] focus:border-[#6ECDC5] outline-none transition-all"
                          value={item?.duration ? item.duration.split(":")[0] : ""}
                          onChange={(e) => {
                            if (!item.isEditMode) return;
                            const newHours = e.target.value.replace(/\D/g, "");
                            const [, minutes = "00"] = item.duration.split(":");
                            setMailData(
                              mailData.map((mail, i) =>
                                i === ind
                                  ? { ...mail, duration: `${newHours}:${minutes}` }
                                  : mail
                              )
                            );
                          }}
                          placeholder="0"
                        />
                        <span className="text-sm text-[#6B7280] font-medium">hrs</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center
                            focus:ring-2 focus:ring-[#6ECDC5] focus:border-[#6ECDC5] outline-none transition-all"
                          value={item?.duration?.split(":")[1] || ""}
                          onChange={(e) => {
                            if (!item.isEditMode) return;
                            const newMinutes = e.target.value.replace(/\D/g, "");
                            const hours = item?.duration?.split(":")[0] || "0";
                            setMailData(
                              mailData.map((mail, i) =>
                                i === ind
                                  ? { ...mail, duration: `${hours}:${newMinutes}` }
                                  : mail
                              )
                            );
                          }}
                          onBlur={(e) => {
                            const minutes = e.target.value.padStart(2, "0");
                            const hours = item?.duration?.split(":")[0] || "00";
                            setMailData(
                              mailData.map((mail, i) =>
                                i === ind
                                  ? { ...mail, duration: `${hours}:${minutes}` }
                                  : mail
                              )
                            );
                          }}
                          placeholder="00"
                        />
                        <span className="text-sm text-[#6B7280] font-medium">min</span>
                      </div>
                    </div>
                  </div>

                  {/* Min Elapsed Time (Redeem only) */}
                  {mailType === "redeem" && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-[#1a1a2e] mb-2">
                        Minimum Elapsed Time
                      </label>
                      <p className="text-xs text-[#6B7280] mb-3">
                        Set how many hours and minutes should elapse after booking before sending redeem mail
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <input
                            type="text"
                            className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center
                              focus:ring-2 focus:ring-[#6ECDC5] focus:border-[#6ECDC5] outline-none transition-all"
                            value={item?.minElapsedTime?.split(":")[0] || ""}
                            onChange={(e) => {
                              if (!item?.isEditMode) return;
                              const newHours = e.target.value.replace(/\D/g, "");
                              const [, minutes = "00"] = item?.minElapsedTime?.split(
                                ":"
                              ) || ["00", "00"];
                              setMailData(
                                mailData.map((mail, i) =>
                                  i === ind
                                    ? {
                                        ...mail,
                                        minElapsedTime: `${newHours}:${minutes}`,
                                      }
                                    : mail
                                )
                              );
                            }}
                            placeholder="0"
                          />
                          <span className="text-sm text-[#6B7280] font-medium">hrs</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="text"
                            className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center
                              focus:ring-2 focus:ring-[#6ECDC5] focus:border-[#6ECDC5] outline-none transition-all"
                            value={item?.minElapsedTime?.split(":")[1] || ""}
                            onChange={(e) => {
                              if (!item?.isEditMode) return;
                              const newMinutes = e.target.value.replace(/\D/g, "");
                              const [hours = "00"] = item?.minElapsedTime?.split(":") || [
                                "00",
                                "00",
                              ];
                              setMailData(
                                mailData.map((mail, i) =>
                                  i === ind
                                    ? {
                                        ...mail,
                                        minElapsedTime: `${hours}:${newMinutes}`,
                                      }
                                    : mail
                                )
                              );
                            }}
                            onBlur={(e) => {
                              const minutes = e.target.value.padStart(2, "0");
                              const [hours = "00"] = item?.minElapsedTime?.split(":") || [
                                "00",
                                "00",
                              ];
                              setMailData(
                                mailData.map((mail, i) =>
                                  i === ind
                                    ? { ...mail, minElapsedTime: `${hours}:${minutes}` }
                                    : mail
                                )
                              );
                            }}
                            placeholder="00"
                          />
                          <span className="text-sm text-[#6B7280] font-medium">min</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tour & Bundle Selection */}
                  {item?.transportation == -1 && (
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="block text-sm font-medium text-[#1a1a2e] mb-3">
                          Select Tours
                        </label>
                        <MultiTourSelection
                          templateData={item}
                          data={
                            templateTours?.find((mail) => mail.templateId === item.id)
                              ?.tours
                          }
                          handleChange={onTourSelect}
                          handleChangeAll={onTourSelectAll}
                          className="w-full"
                        />
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="block text-sm font-medium text-[#1a1a2e] mb-3">
                          Select Bundles
                        </label>
                        <MultiTourSelection
                          templateData={item}
                          data={
                            templateBundles?.find(
                              (mail) => mail.templateId === item.id
                            )?.bundles
                          }
                          handleChange={onBundleSelect}
                          handleChangeAll={onBundleSelectAll}
                          className="w-full"
                        />
                      </div>
                    </div>
                  )}

                  {/* Transportation Mode (Redeem only) */}
                  {mailType === "redeem" && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-[#1a1a2e] mb-2">
                        Mode of Transportation
                      </label>
                      <select
                        onChange={(e: any) => {
                          if (!item.isEditMode) return;
                          console.log("mail1 data", e.target.value, mailData);
                          setMailData(
                            mailData.map((mail, i) =>
                              i === ind
                                ? {
                                    ...mail,
                                    transportation: e.target.value,
                                    tourlist:
                                      e.target.value == -1 ? "" : mail.tourlist,
                                    bundlelist:
                                      e.target.value == -1 ? "" : mail.bundlelist,
                                  }
                                : mail
                            )
                          );
                        }}
                        value={String(item?.transportation || "-1")}
                        className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm
                          focus:ring-2 focus:ring-[#6ECDC5] focus:border-[#6ECDC5] outline-none bg-white"
                      >
                        {[
                          { name: "None", id: -1 },
                          { name: "Walking", id: 1 },
                          { name: "Driving", id: 2 },
                        ].map((elm) => (
                          <option key={elm.id} value={elm.id}>
                            {elm.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Email Templates */}
                  <MailInputComponent
                    key={"Template(s)"}
                    mailData={item}
                    templateData={
                      emailContents.length > 0
                        ? emailContents?.find((mail) => mail.templateId === item.id)
                            ?.template
                        : []
                    }
                    onEmailTemplateChange={onEmailTemplateChange}
                    name={"template"}
                    label={"Template(s)"}
                    placeholder={"Enter template"}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MailTemplate;
